import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  ExportTemplate,
  type WatchDealSelectionExportsSubscription,
  useExportDealSelectionMutation,
  useGetDealSelectionExportDownloadUrlLazyQuery,
  useWatchDealSelectionExportsSubscription,
} from '@/lib/graphql/generated/graphql';
import { trackEvent } from '@/lib/mixpanel';

import { mapI18nLanguageToLanguageIso6391 } from '../utils';

type DealSelectionExportFromSubscription =
  WatchDealSelectionExportsSubscription['dealSelectionExportsUpdated'][number];

// Safety net for the spinner: if the WebSocket subscription never delivers the
// "export ready" event (socket dropped, missed event during a reconnect, …),
// run a one-shot download-URL check at this deadline before failing the UI.
const EXPORT_DEADLINE_MS = 15 * 1000;

export function useDealSelectionExport(
  dealId: string,
  template: ExportTemplate
) {
  const [exportsList, setExportsList] = useState<
    DealSelectionExportFromSubscription[]
  >([]);
  const [isExporting, setIsExportingState] = useState(false);
  const [downloadUrl, setDownloadUrl] = useState<string | null>(null);
  const [error, setError] = useState<Error | null>(null);
  const [pendingExportId, setPendingExportId] = useState<string | null>(null);
  const { i18n, t } = useTranslation();

  const trackedExportIdRef = useRef<string | null>(null);
  const trackedDownloadUrlRef = useRef<string | null>(null);
  // Mirrors `isExporting` so the always-live subscription's onError can read
  // the current value without capturing a stale closure (see below).
  const isExportingRef = useRef(false);
  const setIsExporting = useCallback((value: boolean) => {
    isExportingRef.current = value;
    setIsExportingState(value);
  }, []);
  // Guards the post-mutation phase against double-settling: whichever terminal
  // outcome lands first (download ready, download-url error, subscription drop
  // or the deadline safety net) wins and fires exactly one analytics event; the
  // others become no-ops. Reset at the start of every export.
  const exportSettledRef = useRef(false);

  // 1️⃣ Mutation : onCompleted, on examine le __typename
  const [exportDealSelection] = useExportDealSelectionMutation({
    variables: {
      dealId,
      template,
      language: mapI18nLanguageToLanguageIso6391(i18n.language),
    },
    onCompleted(data) {
      const result = data.exportDealSelection;
      if (result.__typename === 'DomainException') {
        setError(new Error(result.code));
        setIsExporting(false);
        setPendingExportId(null);
        trackEvent('EXPORT_FAILED', {
          dealId,
          template,
          errorCode: result.code,
        });
      } else {
        setError(null);
        setIsExporting(true);
        setPendingExportId(result.id);
      }
    },
    onError(err) {
      setError(err);
      setIsExporting(false);
      setPendingExportId(null);
      trackEvent('EXPORT_FAILED', {
        dealId,
        template,
        errorCode: err.message || 'network_error',
      });
    },
  });

  const [getDownloadUrl, { data: urlData, error: urlError }] =
    useGetDealSelectionExportDownloadUrlLazyQuery({
      fetchPolicy: 'no-cache',
    });

  useEffect(() => {
    if (urlError) {
      if (exportSettledRef.current) return;
      exportSettledRef.current = true;
      setError(urlError);
      setIsExporting(false);
      setPendingExportId(null);
      trackEvent('EXPORT_FAILED', {
        dealId,
        template,
        errorCode: urlError.message || 'download_url_error',
      });
    }
  }, [urlError, dealId, template]);

  useEffect(() => {
    if (
      urlData?.dealSelectionExportDownloadUrl &&
      urlData.dealSelectionExportDownloadUrl !== trackedDownloadUrlRef.current
    ) {
      // A late-arriving URL after the export already failed (e.g. the deadline
      // safety net fired first) must not surprise the user with a download nor
      // emit a second terminal event.
      if (exportSettledRef.current) return;
      exportSettledRef.current = true;
      trackedDownloadUrlRef.current = urlData.dealSelectionExportDownloadUrl;
      setDownloadUrl(urlData.dealSelectionExportDownloadUrl);
      setIsExporting(false);
      setPendingExportId(null);
      trackEvent('EXPORT_DOWNLOADED', { dealId, template });
    }
  }, [urlData, dealId, template]);

  // Deadline safety net: once an export is in flight we give the realtime
  // subscription EXPORT_DEADLINE_MS to deliver the "ready" event. If it never
  // arrives, we run a single download-URL check. If the file is actually ready
  // the existing urlData effect takes over and downloads it; otherwise we stop
  // the spinner, surface a translated error toast and report EXPORT_FAILED so
  // analytics keep a single, consistent failure funnel.
  useEffect(() => {
    if (!isExporting || !pendingExportId) return;
    const exportId = pendingExportId;
    let active = true;

    const timeoutId = window.setTimeout(() => {
      void (async () => {
        // The one-shot check tells us whether the file is actually ready. We
        // treat *any* non-ready outcome the same — an empty response or a
        // thrown network/GraphQL error — so the deadline always resolves to a
        // definite state instead of silently leaving the spinner stuck.
        let isReady = false;
        try {
          const result = await getDownloadUrl({ variables: { exportId } });
          isReady = Boolean(result.data?.dealSelectionExportDownloadUrl);
        } catch {
          // Keep isReady = false; handled as a timeout failure below.
        }
        if (!active) return;
        // File turned out to be ready: the urlData effect downloads it and
        // emits EXPORT_DOWNLOADED, so the safety net stays silent.
        if (isReady) return;
        // Guaranteed terminal event: fire exactly once, even if another branch
        // is racing us.
        if (exportSettledRef.current) return;
        exportSettledRef.current = true;
        setError(new Error(t('general.error_export_timeout')));
        setIsExporting(false);
        setPendingExportId(null);
        trackEvent('EXPORT_FAILED', {
          dealId,
          template,
          errorCode: 'export_timeout',
        });
      })();
    }, EXPORT_DEADLINE_MS);

    return () => {
      active = false;
      window.clearTimeout(timeoutId);
    };
  }, [
    isExporting,
    pendingExportId,
    getDownloadUrl,
    dealId,
    template,
    t,
    setIsExporting,
  ]);

  useWatchDealSelectionExportsSubscription({
    variables: { dealId },
    skip: !dealId,
    onSubscriptionData({ subscriptionData }) {
      const updated = subscriptionData.data?.dealSelectionExportsUpdated;
      if (!updated) return;

      setExportsList(updated);

      const ready = updated.find((e) => e.downloadUrlAvailable);
      if (ready) {
        if (trackedExportIdRef.current !== ready.id) {
          trackedExportIdRef.current = ready.id;
          trackEvent('EXPORT_COMPLETED', {
            dealId,
            template,
            exportId: ready.id,
          });
        }
        getDownloadUrl({ variables: { exportId: ready.id } });
      }
    },
    // This subscription stays live for the whole component lifetime, so this
    // callback also fires on WebSocket drops that happen while no export is
    // running (graphql-ws only surfaces the error after it gives up retrying).
    // Reporting those as EXPORT_FAILED was misleading: it polluted the
    // analytics funnel with phantom failures and flashed an error toast at
    // users who never clicked Export. Only treat the error as an export
    // failure when an export is actually in flight — an idle connection blip
    // is not one. When an export *is* running, surfacing the error still
    // matters so the user isn't stuck on a spinner forever.
    onError(err) {
      if (!isExportingRef.current) return;
      if (exportSettledRef.current) return;
      exportSettledRef.current = true;
      setError(err);
      setIsExporting(false);
      trackEvent('EXPORT_FAILED', {
        dealId,
        template,
        errorCode: err.message || 'subscription_error',
      });
    },
  });

  const startExport = useCallback(() => {
    setError(null);
    setDownloadUrl(null);
    setPendingExportId(null);
    exportSettledRef.current = false;
    trackedExportIdRef.current = null;
    trackedDownloadUrlRef.current = null;
    trackEvent('EXPORT_REQUESTED', {
      dealId,
      template,
      language: mapI18nLanguageToLanguageIso6391(i18n.language),
    });
    exportDealSelection();
  }, [exportDealSelection, dealId, template, i18n.language]);

  return {
    exportsList,
    isExporting,
    downloadUrl,
    error,
    startExport,
  };
}
